/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import AdvertisingTagMounts from '@aglyn/aglyn/app-utils/advertising-tag-mounts'
import { ADVERTISING_TAG_ATTRIBUTE } from '@aglyn/aglyn/app-utils/advertising-tags'
import {
  platformAdvertisingAllowed,
  platformAnalyticsAllowed,
} from '@aglyn/aglyn/app-utils/platform-visitor-consent'
import {
  platformGtmBootSnippet,
  platformGtmScriptSrc,
  resolvePlatformAdvertisingTags,
  resolvePlatformGtmContainerId,
} from '@aglyn/aglyn/app-utils/platform-advertising-tags'
import { VISITOR_CONSENT_CHANGED_EVENT } from '@aglyn/aglyn/app-utils/visitor-consent'
import {
  INTERNAL_ACTOR_VERDICT_EVENT,
  readInternalActorVerdict,
  readInternalTrafficOverride,
  readRememberedInternalActor,
} from '../utils/internal-traffic'
import Script from 'next/script'
import { useCallback, useEffect, useState, type ReactElement } from 'react'

/**
 * The console's consent-gated advertising tags — Meta, Google Ads, LinkedIn
 * and a Google Tag Manager container, on `app.aglyn.com` and `auth.aglyn.com`.
 *
 * ## Why the console has these at all
 *
 * Aglyn advertises, remarkets and retargets across its own surfaces, and the
 * console is one of them — `/signin` is this surface's most-collected page and
 * the whole of the signup funnel lives here. Until now the only advertising
 * enforcement channel in the repository was mounted from the TENANT runtime,
 * so the console carried no advertising vendor of any kind: not because a
 * decision was taken against it, but because there was no mount point.
 *
 * ## The gate is the console's own resolver, asked rather than re-read
 *
 * `platformAdvertisingAllowed()` reads the record the console's posture
 * machinery already wrote — the region endpoint, the prior-consent set, GPC,
 * an explicit choice from the account-menu control or from the CCPA §7015 pill
 * on the unauthenticated pages, and the registrable-domain mirror that carries
 * an answer between `app.` and `auth.`. Nothing here re-reads a cookie or
 * re-derives a posture; a second reading of the same state is how one surface
 * comes to disagree with another about what a visitor said.
 *
 * Advertising is a SEPARATE grant from analytics, and both are represented
 * here: the vendor tags ride `platformAdvertisingAllowed`, the container rides
 * `platformAnalyticsAllowed`. See `platform-advertising-tags.ts` for why a
 * container is gated on analytics and never on something looser.
 *
 * ## Why it re-reads on the consent event rather than on a render
 *
 * The record is written asynchronously — `decidePlatformConsent` has to ask
 * the region endpoint before it can record implied consent for a visitor
 * outside the prior-consent regions — so the answer at first paint is
 * routinely "not yet" and becomes "yes" a moment later with no prop changing.
 * The same event carries the opposite move: a withdrawal from the account menu
 * or the pill. Subscribing to it is what makes both act on THIS pageview.
 *
 * ## Why nothing renders on the server or on the first client render
 *
 * `ready` starts false and is set in an effect. The server has no visitor
 * state to render, and emitting a tag one visitor granted into HTML that any
 * other visitor could be served is the failure the tenant's ISR discipline
 * exists to prevent. Here it also keeps hydration honest: the server and the
 * first client render agree on "nothing yet", and the visitor's own state
 * attaches after.
 *
 * ## Why the nonce is a prop and not read here
 *
 * Every boot below is inline, and the console enforces a nonce'd `script-src`
 * (AGL-2640). Next stamps a nonce onto a `<Script>` only when one reaches it,
 * and the App Router's client head manager carries none — so a pair mounted
 * from an effect, which is the only way this component ever mounts one, is
 * refused unless the value is handed down explicitly. The root layout is the
 * nearest server component, so it reads the header the middleware set and the
 * value arrives here as a prop; a client component has no request to read.
 */
export interface PlatformAdvertisingTagsProps {
  /** The request's CSP nonce, stamped onto every element this mounts. */
  readonly nonce?: string
}

export default function PlatformAdvertisingTags({
  nonce,
}: PlatformAdvertisingTagsProps = {}): ReactElement | null {
  const [ready, setReady] = useState(false)
  // A counter rather than a boolean pair: the verdict is re-read from the
  // resolvers on every render, and this only has to make a render happen. A
  // stored copy of the answer would be a second place for it to live.
  const [, setRevision] = useState(0)

  useEffect(() => {
    setReady(true)
    const sync = () => setRevision((previous) => previous + 1)
    window.addEventListener(VISITOR_CONSENT_CHANGED_EVENT, sync)
    // The claims verdict arrives after this has already rendered once
    // (AGL-3194), and it is the answer that decides whether these tags may
    // exist at all — so it gets a re-resolve exactly like a consent change.
    window.addEventListener(INTERNAL_ACTOR_VERDICT_EVENT, sync)
    return () => {
      window.removeEventListener(VISITOR_CONSENT_CHANGED_EVENT, sync)
      window.removeEventListener(INTERNAL_ACTOR_VERDICT_EVENT, sync)
    }
  }, [])

  // Read FRESH, for the teardown listener inside the shared mount: it fires in
  // the same tick as the record is written, and the verdict computed for the
  // current render is by definition the state before it.
  // Whether this browser is OURS, by either route (AGL-3191).
  //
  // The override alone was not enough, and the reason the module comment gives
  // for it — "it is the BROWSER, not the account: there is no account to
  // consult here" — is true of the marketing site and false of this one. The
  // console is exactly where an account exists, and a staff member signing in
  // is the commonest way for a session to be ours. Before this, they were
  // stamped internal in GA4 by their claims (AGL-1582) and still loaded the
  // Google Ads tags, because those read only the opt-in flag.
  //
  // `readRememberedInternalActor` is the claims verdict in the form this can
  // use: written per origin on every staff or impersonation token read, and
  // synchronous, so it does not have to wait for a token that resolves after
  // the tags would otherwise have mounted. It is CLEARED on a customer token,
  // which is what stops it becoming the sticky flag the override deliberately
  // is — a customer signing in on the same browser is not ours.
  //
  // ⚑ Self-healing rather than immediate: on the very first staff load of a
  // new origin the memory is not written yet, so that one pageview still
  // mounts. Every load after it does not.
  const internalBrowser = useCallback(
    () =>
      readInternalTrafficOverride() ||
      readRememberedInternalActor() ||
      readInternalActorVerdict().internal,
    [],
  )

  const resolve = useCallback(
    () =>
      resolvePlatformAdvertisingTags(
        platformAdvertisingAllowed(),
        undefined,
        undefined,
        internalBrowser(),
      ),
    [internalBrowser],
  )

  // Held while a token read is in flight (AGL-3194). The memory and the
  // override can both be empty on the first staff load of an origin, and a
  // tag mounted before the verdict lands has already sent its hit — sweeping
  // it afterwards does not un-send it. `pending` is true ONLY when there is a
  // real read to wait for, so a signed-out visitor is never held and the
  // `/signin` mount this component is deliberately placed outside the auth
  // gate for is unchanged.
  //
  // Expressed as an empty tag list rather than a suppressed mount, which is
  // the same structural state an ungranted visitor is in: no `<Script>`, so
  // no request reaches a vendor.
  const settled = ready && !readInternalActorVerdict().pending
  const tags = settled ? resolve() : []
  const containerId = settled
    ? resolvePlatformGtmContainerId(
        platformAnalyticsAllowed(),
        undefined,
        undefined,
        internalBrowser(),
      )
    : null

  return (
    <>
      <AdvertisingTagMounts
        active={ready}
        tags={tags}
        resolve={resolve}
        nonce={nonce}
      />
      {/* GOOGLE TAG MANAGER, under the analytics gate and never a looser one.

          Consent Mode v2 is already declared by the time this runs: the
          Firebase services boot pushes `PLATFORM_CONSENT_DEFAULT_COMMANDS`
          onto `dataLayer` before it initializes gtag, which is ahead of
          anything a container can process. That ordering is the whole of its
          correctness — defaults set after a container has loaded are defaults
          its tags have already run past — so this snippet deliberately does
          not re-declare them.

          The container carries the teardown's scope marker even though a
          container is not an `AdvertisingVendor`. `revokeAdvertisingTags`
          never removes it (it acts per vendor), but `GOOGLE_ADS_VENDOR` is
          `alwaysSweep`, so the `_gcl_*` cookies a container's advertising tags
          write are cleared on withdrawal whether or not we ever marked the tag
          that wrote them.

          ⚠️ What a container LOADS is decided in Google's UI and is invisible
          to every spec in this repository. Leaving the id unconfigured is how
          a deployment runs no container; configuring one is an assertion that
          somebody has read what is in it. */}
      {containerId ? (
        <>
          <Script
            id="gtm-init"
            strategy="afterInteractive"
            nonce={nonce}
            {...{ [ADVERTISING_TAG_ATTRIBUTE]: 'gtm' }}
          >
            {platformGtmBootSnippet()}
          </Script>
          <Script
            id="gtm-src"
            strategy="afterInteractive"
            nonce={nonce}
            {...{ [ADVERTISING_TAG_ATTRIBUTE]: 'gtm' }}
            src={platformGtmScriptSrc(containerId)}
          />
        </>
      ) : null}
    </>
  )
}
PlatformAdvertisingTags.displayName = 'PlatformAdvertisingTags'
PlatformAdvertisingTags.aglyn = true
